import React from "react";
import { Badge, Button } from "react-bootstrap";
import { toPlainText } from "../Briefcase/ArtifactTable";
import { RETRO_VOTES_ENABLED } from "./retroConfig";

// Tarjeta bespoke del Tablero de Retrospectiva (spec 006, RF-7..RF-11). Props tipables (listo para TS,
// §6.10): recibe la tarjeta ya resuelta por SP_RETRO_TARJETA_LISTAR + agregados (TotalVotos/TotalLikes/
// MisVotos/YoDiLike, RF-19) y la lista de votantes/likers de esta tarjeta (RF-18, transparencia) resuelta
// por el padre desde SP_RETRO_VOTANTES_LISTAR. El texto SIEMPRE se renderiza como texto plano vía
// `toPlainText` (anti-XSS, H-05/RF-11) — nunca dangerouslySetInnerHTML. El bloque de votos (−/[N]/+, badge
// y tooltip de votantes) se oculta completo con RETRO_VOTES_ENABLED=false (D8-3/RF-29); el like NO se
// toca, sigue visible y funcional siempre.
//
// Props:
//  - tarjeta: { IdTarjeta, IdColumna, Texto, CreadoPor, FechaCreado, ModificadoPor, FechaModificado,
//              TotalVotos, TotalLikes, MisVotos, YoDiLike }
//  - votantesNombres: string[] (nombres de quién votó, RF-18 — ignorado si RETRO_VOTES_ENABLED=false)
//  - likersNombres: string[] (nombres de quién dio like, RF-18)
//  - readOnly: boolean (tablero cerrado, CA-11 — congela los controles en el front además del 409 del SP)
//  - writingEnabled: boolean (timer corriendo, RF-26 UX — congela editar/eliminar en el front además del
//    409 TIMER_NOT_RUNNING del SP; independiente de readOnly)
//  - canAddVote: boolean (presupuesto restante > 0, RF-16 — deshabilita "+" en 0)
//  - onVoteAdd / onVoteRemove: () => void
//  - onLikeToggle: () => void
//  - onEdit / onDelete: () => void
//  - dragHandleProps / draggableProps / innerRef: pasados por Draggable de @hello-pangea/dnd (RF-10)
//  - t: función de traducción (i18next, namespace "retroBoard")
const RetroCard = ({
  tarjeta,
  votantesNombres = [],
  likersNombres = [],
  readOnly = false,
  writingEnabled = true,
  canAddVote = true,
  onVoteAdd,
  onVoteRemove,
  onLikeToggle,
  onEdit,
  onDelete,
  dragHandleProps,
  draggableProps,
  innerRef,
  t,
}) => {
  const texto = toPlainText(tarjeta.Texto);
  const voteTip = votantesNombres.length ? votantesNombres.join(", ") : t("retro_vote_tooltip_empty");
  const likeTip = likersNombres.length ? likersNombres.join(", ") : t("retro_like_tooltip_empty");
  const canWrite = !readOnly && writingEnabled;

  return (
    <div
      ref={innerRef}
      {...draggableProps}
      className="card shadow-sm border-0 mb-2 retro-card animate__animated animate__fadeIn"
    >
      <div className="card-body p-2">
        <div className="d-flex align-items-start">
          <div
            {...dragHandleProps}
            className="retro-card-drag-handle text-muted pr-2"
            title={t("retro_card_drag_hint")}
          >
            <i className="fa fa-grip-vertical" />
          </div>
          <p className="mb-2 flex-grow-1 retro-card-text" style={{ whiteSpace: "pre-wrap" }}>
            {texto}
          </p>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center" style={{ gap: "6px" }}>
            <Button
              size="sm"
              variant={tarjeta.YoDiLike ? "outline-primary" : "outline-secondary"}
              disabled={readOnly}
              onClick={onLikeToggle}
              className="retro-like-btn"
              data-tip={likeTip}
            >
              <i className={`fa fa-heart mr-1 ${tarjeta.YoDiLike ? "text-danger" : ""}`} />
              {tarjeta.TotalLikes || 0}
            </Button>
          </div>

          {RETRO_VOTES_ENABLED && (
            <div className="d-flex align-items-center" style={{ gap: "4px" }} data-tip={voteTip}>
              <Button
                size="sm"
                variant="outline-secondary"
                disabled={readOnly || !(tarjeta.MisVotos > 0)}
                onClick={onVoteRemove}
                title={t("retro_vote_btn_remove")}
              >
                <i className="fa fa-minus" />
              </Button>
              <Badge bg="primary" className="px-2 py-1">
                {tarjeta.TotalVotos || 0}
              </Badge>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={readOnly || !canAddVote}
                onClick={onVoteAdd}
                title={canAddVote ? t("retro_vote_btn_add") : t("retro_vote_budget_exhausted")}
              >
                <i className="fa fa-plus" />
              </Button>
            </div>
          )}

          {canWrite && (
            <div className="d-flex align-items-center" style={{ gap: "4px" }}>
              <Button
                size="sm"
                variant="light"
                onClick={onEdit}
                title={t("retro_card_btn_edit")}
              >
                <i className="fa fa-pen" />
              </Button>
              <Button
                size="sm"
                variant="light"
                onClick={onDelete}
                title={t("retro_card_btn_delete")}
              >
                <i className="fa fa-trash text-danger" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetroCard;
